/**
 * Pure domain logic for multi-column rack allocation and transfer matrix.
 * Supports rapid 1-click assignments, multi-column group transfers, and capacity tracking.
 */

export interface TenantGroupMeta {
  key: string;
  name: string;
  color: string;
  badgeBg: string;
  badgeBorder: string;
  description: string;
}

export const DEFAULT_TENANT_GROUPS: TenantGroupMeta[] = [
  {
    key: 'mine',
    name: 'Своя / Enterprise',
    color: '#3b82f6',
    badgeBg: 'rgba(59, 130, 246, 0.15)',
    badgeBorder: 'rgba(59, 130, 246, 0.4)',
    description: 'Собственное оборудование оператора ЦОД',
  },
  {
    key: 'other',
    name: 'Colocation / Клиенты',
    color: '#a855f7',
    badgeBg: 'rgba(168, 85, 247, 0.15)',
    badgeBorder: 'rgba(168, 85, 247, 0.4)',
    description: 'Стойки внешних заказчиков и арендаторов',
  },
  {
    key: 'reserved',
    name: 'Резерв / Буфер',
    color: '#f59e0b',
    badgeBg: 'rgba(245, 158, 11, 0.15)',
    badgeBorder: 'rgba(245, 158, 11, 0.4)',
    description: 'Зарезервированная площадь и мощность под проекты',
  },
];

const CUSTOM_COLOR_PALETTE = [
  { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.4)' },
  { color: '#ec4899', bg: 'rgba(236, 72, 153, 0.15)', border: 'rgba(236, 72, 153, 0.4)' },
  { color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.15)', border: 'rgba(6, 182, 212, 0.4)' },
  { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.4)' },
  { color: '#14b8a6', bg: 'rgba(20, 184, 166, 0.15)', border: 'rgba(20, 184, 166, 0.4)' },
];

/**
 * Returns metadata for any tenant key (standard or custom).
 */
export function getTenantMeta(key: string, customGroups: TenantGroupMeta[] = []): TenantGroupMeta {
  const normalized = (key || 'mine').trim();
  const standard = DEFAULT_TENANT_GROUPS.find(g => g.key === normalized);
  if (standard) return standard;

  const custom = customGroups.find(g => g.key === normalized);
  if (custom) return custom;

  // Generate deterministic style from key hash
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash << 5) - hash + normalized.charCodeAt(i);
    hash |= 0;
  }
  const pal = CUSTOM_COLOR_PALETTE[Math.abs(hash) % CUSTOM_COLOR_PALETTE.length];

  return {
    key: normalized,
    name: normalized,
    color: pal.color,
    badgeBg: pal.bg,
    badgeBorder: pal.border,
    description: `Клиентская группа: ${normalized}`,
  };
}

/**
 * Groups racks by their tenant.
 * Guarantees that default groups ('mine', 'other', 'reserved') and all custom groups exist.
 */
export function groupRacksByTenant(
  racksInput: Record<string, any> | any[],
  customGroupKeys: string[] = []
): Record<string, any[]> {
  const rackList: any[] = Array.isArray(racksInput) ? racksInput : Object.values(racksInput);

  // Initialize with standard groups
  const groups: Record<string, any[]> = {
    mine: [],
    other: [],
    reserved: [],
  };

  // Add explicit custom groups
  customGroupKeys.forEach(k => {
    const key = k.trim();
    if (key && !groups[key]) {
      groups[key] = [];
    }
  });

  // Distribute racks into groups
  rackList.forEach(rack => {
    const tenantKey = (rack.tenant || 'mine').trim();
    if (!groups[tenantKey]) {
      groups[tenantKey] = [];
    }
    groups[tenantKey].push(rack);
  });

  // Sort racks within each group naturally by row, label, or id
  Object.keys(groups).forEach(key => {
    groups[key].sort((a, b) => {
      const aName = a.label || a.id;
      const bName = b.label || b.id;
      return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
    });
  });

  return groups;
}

/**
 * Transfers a list of racks into a target tenant group.
 * Returns an immutable updated racks dictionary and the count of modified racks.
 */
export function transferRacksToTenant(
  currentRacks: Record<string, any>,
  selectedRackIds: string[],
  targetTenant: string
): { updatedRacks: Record<string, any>; changedCount: number } {
  const updatedRacks = { ...currentRacks };
  const target = targetTenant.trim();
  let changedCount = 0;

  selectedRackIds.forEach(id => {
    const rack = updatedRacks[id];
    if (rack && (rack.tenant || 'mine') !== target) {
      updatedRacks[id] = {
        ...rack,
        tenant: target,
        isDirty: true,
      };
      changedCount++;
    }
  });

  return { updatedRacks, changedCount };
}

/**
 * Filters a rack list by search query matching label, rowId, type or dbId.
 */
export function filterRacks(racks: any[], query: string): any[] {
  if (!query || !query.trim()) return racks;
  const q = query.trim().toLowerCase();

  return racks.filter(r => {
    const labelMatch = (r.label || '').toLowerCase().includes(q);
    const rowMatch = (r.rowId || '').toLowerCase().includes(q);
    const idMatch = (r.id || '').toLowerCase().includes(q);
    const typeMatch = (r.rackType || r.type || '').toLowerCase().includes(q);
    const statusMatch = (r.status || '').toLowerCase().includes(q);
    const roleMatch = (r.role || '').toLowerCase().includes(q);
    return labelMatch || rowMatch || idMatch || typeMatch || statusMatch || roleMatch;
  });
}

/**
 * Calculates allocation statistics across all groups.
 */
export function calculateAllocationStats(racksInput: Record<string, any> | any[]): {
  totalRacks: number;
  itRacks: number;
  facilityRacks: number;
  countByTenant: Record<string, number>;
  powerByTenant: Record<string, number>;
} {
  const rackList: any[] = Array.isArray(racksInput) ? racksInput : Object.values(racksInput);

  let totalRacks = 0;
  let itRacks = 0;
  let facilityRacks = 0;
  const countByTenant: Record<string, number> = {};
  const powerByTenant: Record<string, number> = {};

  rackList.forEach(r => {
    totalRacks++;
    if (r.isFacility) {
      facilityRacks++;
    } else {
      itRacks++;
    }

    const tenant = (r.tenant || 'mine').trim();
    countByTenant[tenant] = (countByTenant[tenant] || 0) + 1;

    const power = typeof r.maxPowerKw === 'number' ? r.maxPowerKw : 5; // default 5kW
    powerByTenant[tenant] = Math.round(((powerByTenant[tenant] || 0) + power) * 10) / 10;
  });

  return {
    totalRacks,
    itRacks,
    facilityRacks,
    countByTenant,
    powerByTenant,
  };
}

/**
 * Finds adjacent tenant group to support keyboard arrow left/right transfer.
 */
export function getNextAdjacentTenant(
  currentTenant: string,
  allTenants: string[],
  direction: 'left' | 'right'
): string | null {
  const idx = allTenants.indexOf(currentTenant);
  if (idx === -1) return null;

  if (direction === 'left') {
    return idx > 0 ? allTenants[idx - 1] : null;
  } else {
    return idx < allTenants.length - 1 ? allTenants[idx + 1] : null;
  }
}
